/**
 * AIC 看板 V2（交易终端风格）纯函数模型。
 * 只做格式化、派生、排序、筛选与降级映射；不发请求、不依赖 React，便于单测。
 * 口径来源：
 * - reports/.../实现契约-AIC看板V2.md §2
 * - reports/.../实现契约-列表页改版-LC2505.md §1（17 列 / 查询项 / 状态派生 / CSV）
 */
import dayjs from 'dayjs'

export const EMPTY = '—'

export const AUDIT_PENDING_TITLE = '审计数据待接入（LC-2505）'

const KB = 1024

const toCount = value => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const isEmptyValue = value => value === null || value === undefined || value === ''

export const termDisplay = (value, emptyText = EMPTY) => (isEmptyValue(value) ? emptyText : value)

export const formatInteger = (value, emptyText = EMPTY) => {
  if (isEmptyValue(value)) return emptyText
  const number = Number(value)
  if (!Number.isFinite(number)) return termDisplay(value, emptyText)
  return number.toLocaleString('en-US')
}

/** 数据量列：≥1 万显示 x.xx万，其余千分位；空值 —。 */
export const formatRowsCount = (value, emptyText = EMPTY) => {
  const count = toCount(value)
  if (count === null) return emptyText
  if (count >= 10000) return `${(count / 10000).toFixed(2)}万`
  return formatInteger(count, emptyText)
}

/** 附件大小。0 表示确实没有数据，null/undefined 表示未知，两者不能混同。 */
export const formatBytes = value => {
  if (isEmptyValue(value)) return EMPTY
  const bytes = Number(value)
  if (!Number.isFinite(bytes) || bytes < 0) return EMPTY
  if (bytes === 0) return '无数据'
  if (bytes < KB) return `${bytes} B`
  if (bytes < KB * KB) return `${Math.round(bytes / KB)} KB`
  if (bytes < KB * KB * KB) return `${(bytes / (KB * KB)).toFixed(1)} MB`
  return `${(bytes / (KB * KB * KB)).toFixed(1)} GB`
}

/**
 * 数据量（表/行/附件组合）：`2 表 · 152 行` / `17 表 + 90 附` / `无表 · 无附件`。
 * tableCount/attachmentCount 全为 null 时说明该接口不提供，渲染 —，不能伪造成"无表"。
 */
export const formatVolume = row => {
  const source = row || {}
  const tables = toCount(source.tableCount)
  const rows = toCount(source.rowCount)
  const attachments = toCount(source.attachmentCount)
  if (tables === null && attachments === null) return EMPTY
  const tableTotal = tables === null ? 0 : tables
  const attachmentTotal = attachments === null ? 0 : attachments
  if (tableTotal <= 0 && attachmentTotal <= 0) return '无表 · 无附件'
  let text = tableTotal > 0 ? `${formatInteger(tableTotal)} 表` : '无表'
  if (tableTotal > 0 && rows !== null) text += ` · ${formatInteger(rows)} 行`
  if (attachmentTotal > 0) text += `${tableTotal > 0 ? ' + ' : ' · '}${formatInteger(attachmentTotal)} 附`
  return text
}

export const UPDATE_PLAN_LABELS = {
  daily: '每天',
  weekly: '每周',
  monthly: '每月',
  quarterly: '每季度',
  irregular: '不定期'
}

export const formatUpdatePlan = (frequency, text) => {
  if (!isEmptyValue(text)) return text
  if (isEmptyValue(frequency)) return '未设置'
  return UPDATE_PLAN_LABELS[frequency] || String(frequency)
}

const formatMonthDay = value => {
  if (!value) return ''
  const date = dayjs(value)
  return date.isValid() ? date.format('MM-DD') : ''
}

/**
 * 更新计划列：`每周 · 截止 11-06`；没有截止时间只显示频率。
 * 频率本身也缺失时沿用「未设置」，不编造周期。
 */
export const formatPlan = (planText, nextExpectedUploadAt) => {
  const plan = isEmptyValue(planText) ? '未设置' : String(planText)
  const deadline = formatMonthDay(nextExpectedUploadAt)
  return deadline ? `${plan} · 截止 ${deadline}` : plan
}

/** 最近上传列：`2026-10-30 17:43`。 */
export const formatDateTimeMinute = (value, emptyText = EMPTY) => {
  if (!value) return emptyText
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : termDisplay(value, emptyText)
}

/** 数据止于列：`2026-10-27`。 */
export const formatDateOnly = (value, emptyText = EMPTY) => {
  if (!value) return emptyText
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD') : termDisplay(value, emptyText)
}

/** 详情页完整时间。 */
export const formatDateTime = (value, emptyText = EMPTY) => {
  if (!value) return emptyText
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss') : termDisplay(value, emptyText)
}

/** md 元信息摘要（详情页用）：`有 · 10,119 字 · 10-30` / `无`；未下发字典信息时 —。 */
export const formatMdSummary = dictionary => {
  if (!dictionary) return EMPTY
  if (dictionary.present !== true) return '无'
  const parts = ['有']
  const length = toCount(dictionary.length)
  if (length !== null && length > 0) parts.push(`${formatInteger(length)} 字`)
  const synced = formatMonthDay(dictionary.syncedAt)
  if (synced) parts.push(synced)
  return parts.join(' · ')
}

/**
 * MD 文档列（列表用）：优先展示后端下发的 `dictionary.excerpt`（前 160 字）。
 * 展示前清掉 markdown 语法噪声（标题 #、引用 >、表格 |、链接/图片），避免列表里出现 `# 标题`。
 * excerpt 未上线时退回真实元信息 `有 · N 字`，不伪造正文；无字典=无，信息缺失=—。
 */
const cleanMdExcerpt = value => String(value || '')
  .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
  .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/[#>*|`_]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

export const formatMdDigest = dictionary => {
  if (!dictionary) return EMPTY
  if (dictionary.present !== true) return '无'
  const excerpt = typeof dictionary.excerpt === 'string' ? cleanMdExcerpt(dictionary.excerpt) : ''
  if (excerpt) {
    const length = toCount(dictionary.length)
    return length !== null && length > excerpt.length ? `${excerpt}…` : excerpt
  }
  return formatMdSummary(dictionary)
}

/**
 * 时间范围（详情页用）：同年只显示 MM-DD，跨年才带年；end 为空显示 `→ 从未同步`。
 * start/end 都为空时不编造范围，返回 —。
 */
export const formatTimeRange = range => {
  const source = range || {}
  const startDate = source.start && dayjs(source.start).isValid() ? dayjs(source.start) : null
  const endDate = source.end && dayjs(source.end).isValid() ? dayjs(source.end) : null
  if (!startDate && !endDate) return EMPTY
  const sameYear = Boolean(startDate && endDate && startDate.year() === endDate.year())
  const endLabel = endDate ? (sameYear ? endDate.format('MM-DD') : endDate.format('YYYY-MM-DD')) : '从未同步'
  if (!startDate) return `${EMPTY} → ${endLabel}`
  const startLabel = sameYear ? startDate.format('MM-DD') : startDate.format('YYYY-MM-DD')
  return `${startLabel} → ${endLabel}`
}

/** 旧版更新列：`10-30 15:37`（详情页沿用）。 */
export const formatUpdatedAt = value => {
  if (!value) return EMPTY
  const date = dayjs(value)
  return date.isValid() ? date.format('MM-DD HH:mm') : termDisplay(value)
}

/** 健康率：接口可能是 0~1 比例或百分数，统一输出两位小数百分比。 */
export const formatRate = value => {
  if (isEmptyValue(value)) return EMPTY
  const number = Number(value)
  if (!Number.isFinite(number)) return termDisplay(value)
  return `${(number <= 1 ? number * 100 : number).toFixed(2)}%`
}

/** 健康状态（详情/旧口径）。 */
export const ANOMALY_META = {
  healthy: { label: '正常', color: '#089981' },
  not_updated: { label: '未更新', color: '#E0A21C' },
  never_uploaded: { label: '从未上传', color: '#F23645' },
  not_configured: { label: '未配置周期', color: '#B2B5BE' }
}

export const anomalyMeta = status => ANOMALY_META[status] || { label: EMPTY, color: '#B2B5BE' }

export const anomalyText = row => {
  const source = row || {}
  if (!source.anomalyStatus) return EMPTY
  return anomalyMeta(source.anomalyStatus).label
}

export const healthLabel = status => ({
  healthy: '正常',
  warning: '异常',
  failing: '故障',
  unknown: '未知'
}[status] || termDisplay(status))

/* ─────────────── LC-2505 逐条台账：来源 / 编号 / 状态派生 ─────────────── */

export const sourceOf = row => {
  const source = row || {}
  return source.projectKind === 'application' ? 'Vibe' : 'AIC'
}

export const projectCode = row => {
  const source = row || {}
  if (source.projectId === null || source.projectId === undefined || source.projectId === '') return EMPTY
  return `${sourceOf(source) === 'Vibe' ? 'VIBE' : 'AIC'}-${source.projectId}`
}

/**
 * 状态派生（诚实口径，见列表页改版契约 §1.3）。
 * 审计态「异常 / 未体检」本期不会出现；不得把 not_configured 伪装成「未体检」。
 */
export const LEDGER_STATUSES = [
  { key: 'not_updated', label: '未更新', color: '#E0A21C', order: 0 },
  { key: 'healthy', label: '正常', color: '#089981', order: 1 },
  { key: 'not_configured', label: '未配置周期', color: '#B2B5BE', order: 2 },
  { key: 'attachments_only', label: '仅附件', color: '#7B8494', order: 3 },
  { key: 'draft', label: '草稿', color: '#C3C9D1', order: 4 },
  { key: 'no_dataset', label: '无数据集', color: '#7B8494', order: 5 }
]

const ledgerStatusByKey = LEDGER_STATUSES.reduce((result, item) => ({ ...result, [item.key]: item }), {})
const ledgerStatusByLabel = LEDGER_STATUSES.reduce((result, item) => ({ ...result, [item.label]: item }), {})

export const ledgerStatusMeta = keyOrLabel => ledgerStatusByKey[keyOrLabel] || ledgerStatusByLabel[keyOrLabel] || null

/** 派生结果返回内部 key，便于筛选/排序；展示用 deriveLedgerStatus 取中文。 */
export const deriveLedgerStatusKey = row => {
  const source = row || {}
  if (source.projectKind === 'application') return 'no_dataset'
  const datasetStatus = !isEmptyValue(source.datasetStatus) ? source.datasetStatus : source.status
  if (datasetStatus === 'draft') return 'draft'
  if (toCount(source.tableCount) === 0 && (toCount(source.attachmentCount) || 0) > 0) return 'attachments_only'
  if (source.anomalyStatus === 'healthy') return 'healthy'
  if (source.anomalyStatus === 'not_updated' || source.anomalyStatus === 'never_uploaded') return 'not_updated'
  if (source.anomalyStatus === 'not_configured') return 'not_configured'
  return null
}

/** 状态中文：正常 / 未更新 / 未配置周期 / 仅附件 / 草稿 / 无数据集；无法判定返回 null。 */
export const deriveLedgerStatus = row => {
  const key = deriveLedgerStatusKey(row)
  return key ? ledgerStatusMeta(key).label : null
}

/* ─────────────────────────── 排序 ─────────────────────────── */

const collator = (() => {
  try {
    return new Intl.Collator('zh-Hans-CN', { numeric: true, sensitivity: 'base' })
  } catch (error) {
    return null
  }
})()

const compareText = (left, right) => {
  const a = isEmptyValue(left) ? '' : String(left)
  const b = isEmptyValue(right) ? '' : String(right)
  return collator ? collator.compare(a, b) : a.localeCompare(b, 'zh-CN')
}

/** 默认复合排序：来源（AIC 在前、Vibe 其后）→ 部门 → 同族 → 负责人 → 数据单元名。 */
const SOURCE_RANK = { AIC: 0, Vibe: 1 }

export const defaultCompare = (left, right) => {
  const a = left || {}
  const b = right || {}
  const rankDiff = (SOURCE_RANK[sourceOf(a)] ?? 9) - (SOURCE_RANK[sourceOf(b)] ?? 9)
  if (rankDiff) return rankDiff
  const byDepartment = compareText(a.department, b.department)
  if (byDepartment) return byDepartment
  const byFamily = compareText(a.family, b.family)
  if (byFamily) return byFamily
  const byOwner = compareText(a.owner, b.owner)
  if (byOwner) return byOwner
  return compareText(a.unitName, b.unitName)
}

export const DEFAULT_SORT = { key: null, direction: null }

const NUMERIC_SORT_KEYS = [
  'tableCount', 'rowCount', 'attachmentBytes', 'fieldCount', 'overdueHours',
  'projectCode', 'dictionaryLength', 'ledgerStatus'
]

export const sortValue = (row, key) => {
  const source = row || {}
  switch (key) {
    case 'source':
      return sourceOf(source)
    case 'projectCode':
      return toCount(source.projectId)
    case 'rowCount':
      return toCount(source.rowCount)
    case 'attachmentBytes':
      return toCount(source.attachmentBytes)
    case 'dictionaryLength':
      return source.dictionary && source.dictionary.present ? toCount(source.dictionary.length) : null
    case 'ledgerStatus': {
      const meta = ledgerStatusMeta(deriveLedgerStatusKey(source))
      return meta ? meta.order : null
    }
    case 'planDeadline':
      return source.nextExpectedUploadAt || null
    case 'dataEnd':
      return source.dataDateRange ? (source.dataDateRange.end || null) : null
    case 'entityTables':
      return Array.isArray(source.entityTables) ? source.entityTables.join(',') : null
    case 'dictionary':
      return source.dictionary && source.dictionary.present ? toCount(source.dictionary.length) : null
    case 'dataDateRange':
      return source.dataDateRange ? (source.dataDateRange.end || source.dataDateRange.start || null) : null
    case 'volume':
      return toCount(source.rowCount) !== null ? toCount(source.rowCount) : toCount(source.tableCount)
    case 'size':
      return toCount(source.attachmentBytes)
    default:
      return source[key]
  }
}

export const sortRows = (rows, sort) => {
  const list = Array.isArray(rows) ? rows.slice() : []
  const safeSort = sort && sort.key && sort.direction ? sort : null
  if (!safeSort) return list.sort(defaultCompare)
  const direction = safeSort.direction === 'desc' ? -1 : 1
  return list.sort((left, right) => {
    const a = sortValue(left, safeSort.key)
    const b = sortValue(right, safeSort.key)
    const aEmpty = isEmptyValue(a)
    const bEmpty = isEmptyValue(b)
    if (aEmpty || bEmpty) {
      if (aEmpty && bEmpty) return defaultCompare(left, right)
      // 空值固定排在最后，不随升降序翻转
      return aEmpty ? 1 : -1
    }
    let compared = 0
    if (NUMERIC_SORT_KEYS.includes(safeSort.key)) {
      compared = Number(a) - Number(b)
      if (!Number.isFinite(compared)) compared = 0
    } else {
      compared = compareText(a, b)
    }
    if (compared) return compared * direction
    return defaultCompare(left, right)
  })
}

export const nextSort = (sort, key) => {
  const current = sort || DEFAULT_SORT
  if (current.key !== key) return { key, direction: 'asc' }
  if (current.direction === 'asc') return { key, direction: 'desc' }
  return DEFAULT_SORT
}

export const sortIndicator = (sort, key) => {
  if (!sort || sort.key !== key) return '↕'
  return sort.direction === 'desc' ? '↓' : '↑'
}

/* ─────────────────────────── 筛选 ─────────────────────────── */

/**
 * 查询项筛选。filters: { source, department, status, owner, keyword }；
 * 第三参数 keyword 覆盖 filters.keyword（契约 §3 签名兼容）。
 * 结论/档位为审计项，不参与筛选，也不伪造值。
 */
export const filterRows = (rows, filters, keywordOverride) => {
  const source = filters || {}
  const keyword = String(keywordOverride === undefined ? (source.keyword || '') : (keywordOverride || ''))
    .trim()
    .toLowerCase()
  const wantedSource = isEmptyValue(source.source) ? '' : source.source
  const wantedDepartment = isEmptyValue(source.department) ? '' : source.department
  const wantedStatus = isEmptyValue(source.status) ? '' : source.status
  const wantedOwner = isEmptyValue(source.owner) ? '' : source.owner
  return (Array.isArray(rows) ? rows : []).filter(row => {
    if (wantedSource && sourceOf(row) !== wantedSource) return false
    if (wantedDepartment && (row.department || '') !== wantedDepartment) return false
    if (wantedStatus && deriveLedgerStatus(row) !== wantedStatus) return false
    if (wantedOwner && (row.owner || '') !== wantedOwner) return false
    if (keyword) {
      const haystack = [
        projectCode(row),
        row.unitName,
        row.owner,
        row.department,
        row.anomalyReason,
        deriveLedgerStatus(row),
        row.category
      ].filter(Boolean).join(' ').toLowerCase()
      if (!haystack.includes(keyword)) return false
    }
    return true
  })
}

export const countLedgerSources = rows => {
  const list = Array.isArray(rows) ? rows : []
  const vibe = list.filter(item => item.projectKind === 'application').length
  return { total: list.length, vibe, aic: list.length - vibe }
}

/* ─────────────────────────── CSV ─────────────────────────── */

export const LEDGER_CSV_HEADERS = [
  '来源', '编号', '数据单元', '数据量', '占用空间', '负责人', '状态', '异常原因/状态说明', '整改需求',
  'MD 文档', '验收结论', 'AI', '部门', '体检三维', '更新计划', '最近上传', '数据止于'
]

/** CSV 单元格：未知与审计列（整改需求/验收结论/AI/体检三维）导出空字符串，不编数据。 */
export const ledgerCsvCellValues = row => {
  const source = row || {}
  const dictionary = source.dictionary
  const mdText = !dictionary
    ? ''
    : (dictionary.present !== true
      ? '无'
      : formatMdDigest(dictionary))
  return [
    sourceOf(source),
    projectCode(source),
    source.unitName || '',
    toCount(source.rowCount) === null ? '' : formatRowsCount(source.rowCount),
    toCount(source.attachmentBytes) === null ? '' : formatBytes(source.attachmentBytes),
    source.owner || '',
    deriveLedgerStatus(source) || '',
    source.anomalyReason || '',
    '',
    mdText,
    '',
    '',
    source.department || '',
    '',
    formatPlan(source.updatePlanText, source.nextExpectedUploadAt),
    formatDateTimeMinute(source.lastSuccessfulUploadAt, ''),
    formatDateOnly(source.dataDateRange && source.dataDateRange.end, '')
  ]
}

const csvQuote = value => `"${String(value === null || value === undefined ? '' : value).replace(/"/g, '""')}"`

/** 导出当前筛选/排序后的全部 17 列；UTF-8 BOM + CRLF。 */
export const ledgerCsv = rows => {
  const lines = [LEDGER_CSV_HEADERS.map(csvQuote).join(',')]
  ;(Array.isArray(rows) ? rows : []).forEach(row => {
    lines.push(ledgerCsvCellValues(row).map(csvQuote).join(','))
  })
  return `\ufeff${lines.join('\r\n')}`
}

const pad2 = value => String(value).padStart(2, '0')

/** 文件名：aic-ledger-YYYYMMDD-HHmm.csv（本地时间）。 */
export const ledgerCsvFilename = (date = new Date()) => {
  const value = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date()
  return `aic-ledger-${value.getFullYear()}${pad2(value.getMonth() + 1)}${pad2(value.getDate())}-${pad2(value.getHours())}${pad2(value.getMinutes())}.csv`
}

/* ─────────────────────────── 数据解包 / 降级映射 ─────────────────────────── */

export const unwrapRows = value => {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'object') return []
  if (Array.isArray(value.list)) return value.list
  if (Array.isArray(value.data)) return value.data
  if (value.data && Array.isArray(value.data.list)) return value.data.list
  return []
}

export const normalizeLedgerRow = row => {
  const source = row || {}
  return {
    ...source,
    projectKind: source.projectKind || 'dataset',
    entityTables: Array.isArray(source.entityTables) ? source.entityTables : [],
    governance: isEmptyValue(source.governance) ? null : source.governance,
    family: isEmptyValue(source.family) ? null : source.family,
    anomalyStatus: source.anomalyStatus || null,
    datasetStatus: isEmptyValue(source.datasetStatus) ? (isEmptyValue(source.status) ? null : source.status) : source.datasetStatus,
    updatePlanText: formatUpdatePlan(source.updateFrequency, source.updatePlanText)
  }
}

/** `/ledger` 响应解包；兼容直接返回数组的旧形态。 */
export const unwrapLedgerPayload = payload => {
  const value = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
  const list = unwrapRows(payload).map(normalizeLedgerRow)
  return {
    list,
    summary: value.summary || null,
    total: toCount(value.total) !== null ? toCount(value.total) : list.length,
    calculatedAt: value.calculatedAt || null
  }
}

export const mapLegacyAnomalyStatus = dataset => {
  if (!dataset) return null
  if (dataset.projectKind === 'application') return null
  if (dataset.healthStatus === 'healthy') return 'healthy'
  if (dataset.healthStatus === 'warning') return 'not_updated'
  return 'not_configured'
}

/**
 * 降级映射：`/ledger` 未上线时把旧 `getAicDashboardDatasets` 行映射成台账行。
 * 只填旧接口真实存在字段，其余置 null/空，由页面渲染 —，不编造。
 */
export const mapLegacyDatasetsToLedger = rows => (Array.isArray(rows) ? rows : []).map(dataset => {
  const isApplication = dataset.projectKind === 'application'
  return {
    projectKind: isApplication ? 'application' : 'dataset',
    datasetId: isEmptyValue(dataset.datasetId) ? null : dataset.datasetId,
    projectId: isEmptyValue(dataset.projectId) ? null : dataset.projectId,
    unitName: dataset.projectName || dataset.unitName || dataset.name || null,
    category: isApplication ? 'Vibe 应用' : null,
    governance: null,
    family: null,
    entityTables: [],
    owner: isEmptyValue(dataset.owner) ? null : dataset.owner,
    dataOwner: isEmptyValue(dataset.dataOwner) ? null : dataset.dataOwner,
    department: dataset.ownerDepartmentName || dataset.department || null,
    businessSubject: isEmptyValue(dataset.businessSubject) ? null : dataset.businessSubject,
    sensitivityLevel: isEmptyValue(dataset.sensitivityLevel) ? null : dataset.sensitivityLevel,
    importMode: isEmptyValue(dataset.defaultImportMode) ? null : dataset.defaultImportMode,
    datasetStatus: isEmptyValue(dataset.status) ? null : dataset.status,
    tableCount: null,
    fieldCount: null,
    rowCount: isEmptyValue(dataset.dataRowCount) ? null : dataset.dataRowCount,
    rowCountSource: null,
    attachmentCount: null,
    attachmentBytes: null,
    updateFrequency: isEmptyValue(dataset.updateFrequency) ? null : dataset.updateFrequency,
    updatePlanText: formatUpdatePlan(dataset.updateFrequency),
    firstSuccessfulUploadAt: dataset.firstSuccessfulUploadAt || null,
    lastSuccessfulUploadAt: dataset.lastSuccessfulUploadAt || null,
    nextExpectedUploadAt: dataset.nextExpectedUploadAt || null,
    dataDateRange: null,
    dictionary: null,
    anomalyStatus: mapLegacyAnomalyStatus(dataset),
    anomalyReason: dataset.healthReason || (isApplication ? '非 AIC 数据集，不参与断更判定' : null),
    overdueHours: isEmptyValue(dataset.overdueHours) ? null : dataset.overdueHours,
    healthStatus: isEmptyValue(dataset.healthStatus) ? null : dataset.healthStatus,
    degraded: true
  }
})
